import {
  createContext,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'

import { isBookingPreviewOpenSelector } from 'shared/shell_layout'

import {
  DEFAULT_SIDE_PANEL_WIDTH_PX,
  MIN_CENTER_PANEL_WIDTH_PX,
  MIN_SIDE_PANEL_WIDTH_PX,
  RESIZE_HANDLE_WIDTH_PX,
  ShellBodyLayoutMode,
} from './constants'
import { parseCssWidthToPx } from './parseCssWidthToPx'
import { resolveLayoutMode } from './resolveLayoutMode'

export interface ShellBodyResizeContextValue {
  enabled: boolean
  layoutMode: ShellBodyLayoutMode
  isDragging: boolean
  sidePanelWidthPx: number
  centerPanelCollapsed: boolean
  sidePanelOverlay: boolean
  resizeHandleProps: {
    role: 'separator'
    'aria-orientation': 'vertical'
    'aria-valuenow': number
    'aria-valuemin': number
    'aria-valuemax': number
    tabIndex: number
    onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void
  }
}

const defaultContextValue: ShellBodyResizeContextValue = {
  enabled: false,
  layoutMode: 'split',
  isDragging: false,
  sidePanelWidthPx: DEFAULT_SIDE_PANEL_WIDTH_PX,
  centerPanelCollapsed: false,
  sidePanelOverlay: false,
  resizeHandleProps: {
    role: 'separator',
    'aria-orientation': 'vertical',
    'aria-valuenow': DEFAULT_SIDE_PANEL_WIDTH_PX,
    'aria-valuemin': MIN_SIDE_PANEL_WIDTH_PX,
    'aria-valuemax': DEFAULT_SIDE_PANEL_WIDTH_PX,
    tabIndex: -1,
    onPointerDown: () => {},
  },
}

const ShellBodyResizeContext = createContext<ShellBodyResizeContextValue>(defaultContextValue)

function getFlexGapPx(element: HTMLElement): number {
  const style = getComputedStyle(element)
  const gap = parseFloat(style.gap || style.columnGap || '0')
  return Number.isFinite(gap) ? gap : 0
}

interface ShellBodyResizeProviderProps {
  bodyRef: RefObject<HTMLElement | null>
  enabled: boolean
  presetWidthCss: string
  /** When true (e.g. AI chat expand), use center-collapsed overlay like legacy `railExpanded` full width. */
  railExpanded?: boolean
  /** Called when the user drags out of overlay while `railExpanded` is still in the URL. */
  onRailExpandedLayoutExit?: () => void
  children: ReactNode
}

export const ShellBodyResizeProvider = ({
  bodyRef,
  enabled,
  presetWidthCss,
  railExpanded = false,
  onRailExpandedLayoutExit,
  children,
}: ShellBodyResizeProviderProps) => {
  const [userWidthPx, setUserWidthPx] = useState<number | null>(null)
  const [layoutMode, setLayoutMode] = useState<ShellBodyLayoutMode>('split')
  const [isDragging, setIsDragging] = useState(false)
  const [bodyWidthPx, setBodyWidthPx] = useState(0)
  const isBookingPreviewOpen = useSelector(isBookingPreviewOpenSelector)
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null)
  const prevLayoutModeRef = useRef<ShellBodyLayoutMode>('split')
  const prevRailExpandedRef = useRef(railExpanded)
  const prevBookingPreviewOpenRef = useRef(false)
  const savedLayoutBeforePreviewRef = useRef<{
    layoutMode: ShellBodyLayoutMode
    userWidthPx: number | null
  } | null>(null)
  const layoutSnapshotRef = useRef({ layoutMode, userWidthPx })
  layoutSnapshotRef.current = { layoutMode, userWidthPx }

  const presetWidthPx = useMemo(
    () => parseCssWidthToPx(presetWidthCss, { viewportWidth: window.innerWidth }),
    [presetWidthCss],
  )

  useEffect(() => {
    setUserWidthPx(null)
  }, [presetWidthCss])

  useEffect(() => {
    if (enabled) {
      if (railExpanded) {
        setLayoutMode('center-collapsed')
      } else if (prevRailExpandedRef.current) {
        // Minimize (railExpanded cleared) — not when a drag ends without railExpanded.
        setLayoutMode('split')
      }
    } else if (!railExpanded) {
      setLayoutMode('split')
      setUserWidthPx(null)
    }
    prevRailExpandedRef.current = railExpanded

    const shouldMeasureBody = enabled || railExpanded
    if (!shouldMeasureBody) return undefined

    const element = bodyRef.current
    if (!element) return undefined

    const updateBodyWidth = () => {
      setBodyWidthPx(element.clientWidth)
    }

    updateBodyWidth()
    const observer = new ResizeObserver(updateBodyWidth)
    observer.observe(element)
    return () => observer.disconnect()
  }, [bodyRef, enabled, railExpanded])

  useEffect(() => {
    if (!enabled) {
      savedLayoutBeforePreviewRef.current = null
      prevBookingPreviewOpenRef.current = false
      return
    }

    const wasOpen = prevBookingPreviewOpenRef.current
    prevBookingPreviewOpenRef.current = isBookingPreviewOpen

    if (isBookingPreviewOpen && !wasOpen) {
      savedLayoutBeforePreviewRef.current = { ...layoutSnapshotRef.current }
      setLayoutMode('split')
      setUserWidthPx(MIN_SIDE_PANEL_WIDTH_PX)
      return
    }

    if (!isBookingPreviewOpen && wasOpen && savedLayoutBeforePreviewRef.current) {
      const saved = savedLayoutBeforePreviewRef.current
      savedLayoutBeforePreviewRef.current = null
      setLayoutMode(saved.layoutMode)
      setUserWidthPx(saved.userWidthPx)
    }
  }, [enabled, isBookingPreviewOpen])

  useEffect(() => {
    if (!enabled || !railExpanded || !onRailExpandedLayoutExit) {
      prevLayoutModeRef.current = layoutMode
      return
    }

    if (
      prevLayoutModeRef.current === 'center-collapsed'
      && layoutMode === 'split'
      && !isDragging
    ) {
      onRailExpandedLayoutExit()
    }

    prevLayoutModeRef.current = layoutMode
  }, [enabled, isDragging, layoutMode, onRailExpandedLayoutExit, railExpanded])

  const bounds = useMemo(() => {
    const gapPx = bodyRef.current ? getFlexGapPx(bodyRef.current) : 0
    const segmentCount = 3
    const gapTotal = gapPx * (segmentCount - 1)
    const available = Math.max(0, bodyWidthPx - gapTotal - RESIZE_HANDLE_WIDTH_PX)
    const minSide = MIN_SIDE_PANEL_WIDTH_PX
    const maxSide = Math.max(minSide, available - MIN_CENTER_PANEL_WIDTH_PX)
    return { available, minSide, maxSide, gapPx }
  }, [bodyRef, bodyWidthPx])

  const splitSideWidthPx = useMemo(() => {
    if (userWidthPx != null) {
      return Math.min(bounds.maxSide, Math.max(bounds.minSide, userWidthPx))
    }
    // Before the user drags, open at the standard side panel width (not rail-expanded preset).
    return Math.min(bounds.maxSide, DEFAULT_SIDE_PANEL_WIDTH_PX)
  }, [bounds.maxSide, bounds.minSide, userWidthPx])

  const { sidePanelWidthPx, resolvedLayoutMode } = useMemo(() => {
    // `railExpanded` (AI chat expand) must apply on the same render as the URL toggle — not
    // one frame after `setLayoutMode`, or the panel stays at ~448px and 780px content max-width
    // never takes effect.
    const mode: ShellBodyLayoutMode = railExpanded ? 'center-collapsed' : layoutMode

    if (bounds.available <= 0) {
      return {
        sidePanelWidthPx: presetWidthPx,
        resolvedLayoutMode: mode,
      }
    }

    if (!enabled) {
      if (mode === 'center-collapsed') {
        return {
          sidePanelWidthPx: bodyWidthPx,
          resolvedLayoutMode: mode,
        }
      }
      return {
        sidePanelWidthPx: presetWidthPx,
        resolvedLayoutMode: mode,
      }
    }

    // `layoutMode` is updated from pointer drags; do not re-derive from `splitSideWidthPx`
    // (it is clamped to split bounds and would never enter collapse modes).
    if (mode === 'center-collapsed') {
      return {
        sidePanelWidthPx: Math.max(0, bodyWidthPx - RESIZE_HANDLE_WIDTH_PX),
        resolvedLayoutMode: mode,
      }
    }

    return {
      sidePanelWidthPx: splitSideWidthPx,
      resolvedLayoutMode: 'split' as ShellBodyLayoutMode,
    }
  }, [bodyWidthPx, bounds.available, enabled, layoutMode, presetWidthPx, railExpanded, splitSideWidthPx])

  const commitWidthFromPointer = useCallback(
    (clientX: number) => {
      const body = bodyRef.current
      if (!body) return

      const bodyRect = body.getBoundingClientRect()
      const gapPx = getFlexGapPx(body)
      const gapTotal = gapPx * 2
      const available = Math.max(0, bodyRect.width - gapTotal - RESIZE_HANDLE_WIDTH_PX)
      const rawSide = bodyRect.right - clientX
      const clampedSide = Math.min(available, Math.max(0, rawSide))
      const centerWidthPx = available - clampedSide
      const mode = resolveLayoutMode(clampedSide, centerWidthPx)

      setLayoutMode(mode)
      if (mode === 'split') {
        setUserWidthPx(Math.min(bounds.maxSide, Math.max(bounds.minSide, clampedSide)))
      }
    },
    [bodyRef, bounds.maxSide, bounds.minSide],
  )

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (!enabled) return
      event.preventDefault()
      event.currentTarget.setPointerCapture(event.pointerId)
      dragStateRef.current = {
        startX: event.clientX,
        startWidth: splitSideWidthPx,
      }
      setIsDragging(true)
    },
    [enabled, splitSideWidthPx],
  )

  useEffect(() => {
    if (!isDragging) return undefined

    const onPointerMove = (event: PointerEvent) => {
      commitWidthFromPointer(event.clientX)
    }

    const onPointerUp = (event: PointerEvent) => {
      commitWidthFromPointer(event.clientX)
      dragStateRef.current = null
      setIsDragging(false)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)

    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    }
  }, [commitWidthFromPointer, isDragging])

  const contextValue = useMemo<ShellBodyResizeContextValue>(() => {
    const centerPanelCollapsed = resolvedLayoutMode === 'center-collapsed'
    const sidePanelOverlay = resolvedLayoutMode === 'center-collapsed'

    return {
      enabled,
      layoutMode: resolvedLayoutMode,
      isDragging,
      sidePanelWidthPx,
      centerPanelCollapsed,
      sidePanelOverlay,
      resizeHandleProps: {
        role: 'separator',
        'aria-orientation': 'vertical',
        'aria-valuenow': Math.round(sidePanelWidthPx),
        'aria-valuemin': bounds.minSide,
        'aria-valuemax': Math.round(bounds.maxSide || sidePanelWidthPx),
        tabIndex: enabled ? 0 : -1,
        onPointerDown,
      },
    }
  }, [
    bounds.maxSide,
    bounds.minSide,
    enabled,
    isDragging,
    onPointerDown,
    resolvedLayoutMode,
    sidePanelWidthPx,
  ])

  return (
    <ShellBodyResizeContext.Provider value={contextValue}>
      {children}
    </ShellBodyResizeContext.Provider>
  )
}

export function useShellBodyResize(): ShellBodyResizeContextValue {
  return useContext(ShellBodyResizeContext)
}
